import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, Users, Target, Zap, Award, AlertTriangle, CheckCircle, Clock, MapPin, Share2, Heart, Eye, MousePointer } from 'lucide-react';

const FNMSponsorDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [resourceAllocation, setResourceAllocation] = useState({ gold: 65, energy: 35 });
  const [timeRange, setTimeRange] = useState('24h');

  // Mock data
  const engagementData = [
    { time: '00:00', engagement: 2400, category: 2100 },
    { time: '04:00', engagement: 1398, category: 1800 },
    { time: '08:00', engagement: 9800, category: 8500 },
    { time: '12:00', engagement: 3908, category: 4200 },
    { time: '16:00', engagement: 4800, category: 4100 },
    { time: '20:00', engagement: 3800, category: 3500 },
  ];

  const spendData = [
    { day: 'Mon', actual: 8500, predicted: 8200, budget: 10000 },
    { day: 'Tue', actual: 9200, predicted: 9100, budget: 10000 },
    { day: 'Wed', actual: 8800, predicted: 9500, budget: 10000 },
    { day: 'Thu', actual: 9600, predicted: 9800, budget: 10000 },
    { day: 'Fri', actual: 11200, predicted: 10500, budget: 10000 },
    { day: 'Sat', actual: 0, predicted: 12000, budget: 10000 },
    { day: 'Sun', actual: 0, predicted: 11500, budget: 10000 },
  ];

  const demographics = [
    { name: '18-24', value: 35, color: '#8b5cf6' },
    { name: '25-34', value: 40, color: '#06b6d4' },
    { name: '35-44', value: 20, color: '#10b981' },
    { name: '45+', value: 5, color: '#f59e0b' },
  ];

  const handleResourceChange = (resource, value) => {
    const newValue = Math.max(0, Math.min(100, value));
    const otherResource = resource === 'gold' ? 'energy' : 'gold';
    const otherValue = 100 - newValue;
    
    setResourceAllocation({
      [resource]: newValue,
      [otherResource]: otherValue
    });
  };

  const MetricCard = ({ title, value, change, icon: Icon, trend, benchmark }) => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {benchmark && (
            <p className="text-xs text-gray-500 mt-1">Category avg: {benchmark}</p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${trend === 'up' ? 'bg-green-50' : trend === 'down' ? 'bg-red-50' : 'bg-blue-50'}`}>
          <Icon className={`w-5 h-5 ${trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-blue-600'}`} />
        </div>
      </div>
      <div className="flex items-center mt-3">
        {trend === 'up' ? (
          <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
        ) : (
          <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
        )}
        <span className={`text-sm font-medium ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
          {change}
        </span>
        <span className="text-sm text-gray-500 ml-1">vs category</span>
      </div>
    </div>
  );

  const AlertCard = ({ type, message, action }) => (
    <div className={`p-4 rounded-lg border-l-4 ${
      type === 'warning' ? 'bg-amber-50 border-amber-400' : 
      type === 'success' ? 'bg-green-50 border-green-400' : 
      'bg-blue-50 border-blue-400'
    }`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {type === 'warning' ? (
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          ) : type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : (
            <TrendingUp className="w-5 h-5 text-blue-600" />
          )}
        </div>
        <div className="ml-3 flex-1">
          <p className={`text-sm font-medium ${
            type === 'warning' ? 'text-amber-800' : 
            type === 'success' ? 'text-green-800' : 
            'text-blue-800'
          }`}>
            {message}
          </p>
          {action && (
            <button className={`mt-2 text-xs font-medium underline ${
              type === 'warning' ? 'text-amber-700' : 
              type === 'success' ? 'text-green-700' : 
              'text-blue-700'
            }`}>
              {action}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">FNM</span>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Sponsor Dashboard</h1>
                <p className="text-sm text-gray-500">McDonald's Campaign Analytics</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <select 
                value={timeRange} 
                onChange={(e) => setTimeRange(e.target.value)}
                className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="1h">Last Hour</option>
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
              </select>
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <nav className="flex space-x-1">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'allocation', label: 'Resource Allocation' },
            { id: 'engagement', label: 'Engagement Analytics' },
            { id: 'demographics', label: 'Demographics' },
            { id: 'insights', label: 'Actionable Insights' },
            { id: 'benchmarks', label: 'Live Benchmarks' },
            { id: 'assets', label: 'Custom Assets' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-purple-100 text-purple-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-12">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Campaign Health Score */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold mb-2">Campaign Health Score</h2>
                  <div className="flex items-end space-x-2">
                    <span className="text-4xl font-bold">87</span>
                    <span className="text-lg opacity-80">/100</span>
                  </div>
                  <p className="text-sm opacity-80 mt-1">23% above category average</p>
                </div>
                <div className="text-right">
                  <div className="w-16 h-16 rounded-full border-4 border-white/30 flex items-center justify-center mb-2">
                    <Award className="w-8 h-8" />
                  </div>
                  <p className="text-sm">Top 15% performer</p>
                </div>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard
                title="Active Players Now"
                value="12,847"
                change="+23%"
                icon={Users}
                trend="up"
                benchmark="9,200"
              />
              <MetricCard
                title="Today's Spend"
                value="$8,420"
                change="+15%"
                icon={DollarSign}
                trend="up"
                benchmark="$7,100"
              />
              <MetricCard
                title="Engagement Rate"
                value="23.4%"
                change="+8%"
                icon={Target}
                trend="up"
                benchmark="18.7%"
              />
              <MetricCard
                title="Authenticity Score"
                value="91/100"
                change="+12%"
                icon={Heart}
                trend="up"
                benchmark="78/100"
              />
            </div>

            {/* Alerts */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900">Smart Alerts</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <AlertCard
                  type="success"
                  message="Engagement 34% above category peak hours (11am-1pm)"
                  action="View optimization opportunities"
                />
                <AlertCard
                  type="info"
                  message="QSR category trending +18% this week - optimal time for budget increase"
                  action="Adjust allocation"
                />
              </div>
            </div>

            {/* Performance Chart */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Engagement vs Category Average</h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={engagementData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Area type="monotone" dataKey="category" stackId="1" stroke="#94a3b8" fill="#e2e8f0" />
                  <Area type="monotone" dataKey="engagement" stackId="2" stroke="#8b5cf6" fill="#a855f7" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {activeTab === 'allocation' && (
          <div className="space-y-6">
            {/* Resource Allocation Controls */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Live Resource Allocation</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">Gold Rewards</label>
                      <span className="text-sm text-gray-500">{resourceAllocation.gold}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={resourceAllocation.gold}
                      onChange={(e) => handleResourceChange('gold', parseInt(e.target.value))}
                      className="w-full h-2 bg-gradient-to-r from-yellow-200 to-yellow-500 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">Energy Rewards</label>
                      <span className="text-sm text-gray-500">{resourceAllocation.energy}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={resourceAllocation.energy}
                      onChange={(e) => handleResourceChange('energy', parseInt(e.target.value))}
                      className="w-full h-2 bg-gradient-to-r from-blue-200 to-blue-500 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Impact Prediction</h4>
                    <p className="text-sm text-gray-600">
                      Increasing gold allocation to {resourceAllocation.gold}% is predicted to:
                    </p>
                    <ul className="text-sm text-gray-600 mt-2 space-y-1">
                      <li>• Boost check-ins by ~8%</li>
                      <li>• Increase session time by ~12%</li>
                      <li>• Cost +$1,200/day</li>
                    </ul>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Resource Performance</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                      <div>
                        <p className="font-medium text-yellow-800">Gold Rewards</p>
                        <p className="text-sm text-yellow-600">+23% vs category avg</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-yellow-800">{resourceAllocation.gold}%</p>
                        <p className="text-sm text-yellow-600">allocation</p>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <div>
                        <p className="font-medium text-blue-800">Energy Rewards</p>
                        <p className="text-sm text-blue-600">+15% vs category avg</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-blue-800">{resourceAllocation.energy}%</p>
                        <p className="text-sm text-blue-600">allocation</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Budget Forecasting */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Spend Forecast</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={spendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="budget" fill="#e5e7eb" />
                  <Bar dataKey="predicted" fill="#06b6d4" />
                  <Bar dataKey="actual" fill="#8b5cf6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {activeTab === 'demographics' && (
          <div className="space-y-6">
            {/* Market Overview */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Global Market Demographics</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-600">23</div>
                  <div className="text-sm text-blue-800">Active Markets</div>
                  <div className="text-xs text-blue-600 mt-1">Across 8 countries</div>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">847K</div>
                  <div className="text-sm text-green-800">Total Active Players</div>
                  <div className="text-xs text-green-600 mt-1">+23% this month</div>
                </div>
                
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-purple-600">28.3</div>
                  <div className="text-sm text-purple-800">Avg. Age</div>
                  <div className="text-xs text-purple-600 mt-1">Perfect target demo</div>
                </div>
                
                <div className="bg-amber-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-amber-600">$74K</div>
                  <div className="text-sm text-amber-800">Avg. Household Income</div>
                  <div className="text-xs text-amber-600 mt-1">Above category avg</div>
                </div>
              </div>

              {/* Interactive Market Selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Market</label>
                <select className="w-full md:w-64 bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
                  <option>All Markets</option>
                  <option>United States</option>
                  <option>Los Angeles, CA</option>
                  <option>Chicago, IL</option>
                  <option>New York, NY</option>
                  <option>Houston, TX</option>
                  <option>Phoenix, AZ</option>
                  <option>Canada</option>
                  <option>Mexico</option>
                </select>
              </div>

              {/* Demographics Breakdown */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-4">Age Distribution</h4>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={[
                      { age: '13-17', players: 85000, income: 0 },
                      { age: '18-24', players: 296450, income: 42000 },
                      { age: '25-34', players: 338800, income: 68000 },
                      { age: '35-44', players: 169400, income: 89000 },
                      { age: '45-54', players: 42350, income: 95000 },
                      { age: '55+', players: 21150, income: 78000 }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="age" />
                      <YAxis />
                      <Tooltip formatter={(value, name) => [value.toLocaleString(), name === 'players' ? 'Players' : 'Avg Income']} />
                      <Bar dataKey="players" fill="#8b5cf6" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-4">Gender & Income</h4>
                  <div className="space-y-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-blue-900">Male Players</span>
                        <span className="text-blue-600 font-bold">58.3%</span>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-2">
                        <div className="w-3/5 h-2 bg-blue-500 rounded-full"></div>
                      </div>
                      <p className="text-sm text-blue-700 mt-1">Avg income: $71,200</p>
                    </div>
                    
                    <div className="bg-pink-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-pink-900">Female Players</span>
                        <span className="text-pink-600 font-bold">41.7%</span>
                      </div>
                      <div className="w-full bg-pink-200 rounded-full h-2">
                        <div className="w-2/5 h-2 bg-pink-500 rounded-full"></div>
                      </div>
                      <p className="text-sm text-pink-700 mt-1">Avg income: $76,800</p>
                    </div>
                    
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h5 className="font-medium text-green-900 mb-2">Spending Behavior</h5>
                      <div className="text-sm text-green-700 space-y-1">
                        <p>• 73% make food purchases 3+ times/week</p>
                        <p>• 89% use mobile apps for ordering</p>
                        <p>• 67% influenced by social media</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Geographic Heatmap */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h4 className="font-semibold text-gray-900 mb-4">Geographic Performance Heatmap</h4>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <div className="bg-gray-100 rounded-lg p-8 text-center">
                    <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600">Interactive map would go here</p>
                    <p className="text-sm text-gray-500">Showing engagement intensity by region</p>
                  </div>
                </div>
                
                <div>
                  <h5 className="font-medium text-gray-800 mb-3">Top Performing Cities</h5>
                  <div className="space-y-3">
                    {[
                      { city: 'Austin, TX', engagement: 94, players: '23.4K', growth: '+34%' },
                      { city: 'Seattle, WA', engagement: 91, players: '31.2K', growth: '+28%' },
                      { city: 'Denver, CO', engagement: 89, players: '19.8K', growth: '+41%' },
                      { city: 'Atlanta, GA', engagement: 87, players: '28.1K', growth: '+22%' },
                      { city: 'Portland, OR', engagement: 85, players: '16.7K', growth: '+38%' }
                    ].map((city, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-sm">{city.city}</span>
                          <span className="text-xs text-green-600 font-medium">{city.growth}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">{city.players} players</span>
                          <span className="text-xs font-medium">Score: {city.engagement}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1 mt-2">
                          <div 
                            className="h-1 bg-gradient-to-r from-green-400 to-green-600 rounded-full" 
                            style={{ width: `${city.engagement}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Psychographic Insights */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-4">Lifestyle Segments</h4>
                <div className="space-y-4">
                  {[
                    { 
                      segment: 'Tech-Savvy Millennials', 
                      percent: 34, 
                      traits: ['Early adopters', 'Social media active', 'Urban lifestyle'],
                      color: 'blue'
                    },
                    { 
                      segment: 'Busy Professionals', 
                      percent: 28, 
                      traits: ['Time-conscious', 'Mobile-first', 'Quality focused'],
                      color: 'purple' 
                    },
                    { 
                      segment: 'College Students', 
                      percent: 23, 
                      traits: ['Budget-conscious', 'Social influence', 'Brand loyal'],
                      color: 'green'
                    },
                    { 
                      segment: 'Gaming Enthusiasts', 
                      percent: 15, 
                      traits: ['Heavy gamers', 'In-app purchasers', 'Community driven'],
                      color: 'amber'
                    }
                  ].map((segment, index) => (
                    <div key={index} className={`bg-${segment.color}-50 p-4 rounded-lg`}>
                      <div className="flex justify-between items-center mb-2">
                        <span className={`font-medium text-${segment.color}-900`}>{segment.segment}</span>
                        <span className={`text-${segment.color}-600 font-bold`}>{segment.percent}%</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {segment.traits.map((trait, i) => (
                          <span key={i} className={`text-xs bg-${segment.color}-100 text-${segment.color}-700 px-2 py-1 rounded`}>
                            {trait}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-4">Device & Platform Usage</h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Mobile (iOS)</span>
                      <span className="text-sm text-gray-600">47%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="w-1/2 h-2 bg-blue-500 rounded-full"></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Mobile (Android)</span>
                      <span className="text-sm text-gray-600">31%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="w-1/3 h-2 bg-green-500 rounded-full"></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Web Browser</span>
                      <span className="text-sm text-gray-600">22%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="w-1/5 h-2 bg-purple-500 rounded-full"></div>
                    </div>
                  </div>

                  <div className="mt-6 bg-gray-50 p-3 rounded-lg">
                    <h5 className="font-medium text-gray-800 mb-2">Peak Usage Times</h5>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>• Mobile: 7-9 AM, 12-1 PM, 6-10 PM</p>
                      <p>• Web: 9 AM-5 PM (work hours)</p>
                      <p>• Weekend: 10 AM-2 PM, 7-11 PM</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'insights' && (
          <div className="space-y-6">
            {/* AI-Powered Recommendations */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-semibold">AI-Powered Actionable Insights</h3>
              </div>
              <p className="text-indigo-100">Smart recommendations based on real-time performance data and market intelligence</p>
            </div>

            {/* Priority Actions */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h4 className="font-semibold text-gray-900 mb-4">🔥 Priority Actions (Next 24 Hours)</h4>
              <div className="space-y-4">
                <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h5 className="font-medium text-red-900">Urgent: Increase Weekend Budget</h5>
                      <p className="text-sm text-red-700 mt-1">
                        Weekend engagement is 43% above weekday average, but current allocation only increases budget by 15%. 
                        Recommendation: Boost weekend budget by 35% for optimal ROI.
                      </p>
                      <div className="mt-2 text-sm text-red-600">
                        <strong>Expected Impact:</strong> +$12K revenue, +2,800 new check-ins
                      </div>
                    </div>
                    <button className="ml-4 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700">
                      Apply Now
                    </button>
                  </div>
                </div>

                <div className="border-l-4 border-amber-500 bg-amber-50 p-4 rounded-r-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h5 className="font-medium text-amber-900">Optimize Resource Mix in Austin</h5>
                      <p className="text-sm text-amber-700 mt-1">
                        Austin market responds 67% better to energy rewards vs. gold. Current 65/35 split underperforming.
                        Recommendation: Flip to 35/65 gold/energy split for Austin specifically.
                      </p>
                      <div className="mt-2 text-sm text-amber-600">
                        <strong>Expected Impact:</strong> +28% engagement, +$3.2K weekly revenue
                      </div>
                    </div>
                    <button className="ml-4 bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700">
                      Test Split
                    </button>
                  </div>
                </div>

                <div className="border-l-4 border-green-500 bg-green-50 p-4 rounded-r-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h5 className="font-medium text-green-900">Launch College Campaign</h5>
                      <p className="text-sm text-green-700 mt-1">
                        College towns show 52% higher engagement but represent only 12% of current spend. 
                        Back-to-school season starting - optimal timing for campus-focused campaign.
                      </p>
                      <div className="mt-2 text-sm text-green-600">
                        <strong>Expected Impact:</strong> +15K new users, +$8K weekly revenue
                      </div>
                    </div>
                    <button className="ml-4 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700">
                      Plan Campaign
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Strategic Opportunities */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-4">🎯 Strategic Opportunities</h4>
                <div className="space-y-3">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <h5 className="font-medium text-blue-900">Untapped Demographics</h5>
                    <p className="text-sm text-blue-700 mt-1">
                      45+ age group shows 23% higher purchase intent but only 6% of current reach
                    </p>
                    <button className="text-blue-600 text-xs font-medium mt-2">Explore targeting →</button>
                  </div>
                  
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <h5 className="font-medium text-purple-900">Cross-Promotion Potential</h5>
                    <p className="text-sm text-purple-700 mt-1">
                      67% of your players also engage with coffee shop brands - partnership opportunity
                    </p>
                    <button className="text-purple-600 text-xs font-medium mt-2">View partnerships →</button>
                  </div>
                  
                  <div className="bg-green-50 p-3 rounded-lg">
                    <h5 className="font-medium text-green-900">Seasonal Optimization</h5>
                    <p className="text-sm text-green-700 mt-1">
                      Summer months show 34% higher engagement - plan budget reallocation now
                    </p>
                    <button className="text-green-600 text-xs font-medium mt-2">Plan seasonal strategy →</button>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-4">⚡ Quick Wins</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Lunch Hour Boost</p>
                      <p className="text-sm text-gray-600">Increase 11AM-1PM allocation</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-green-600">+$2.1K/day</p>
                      <button className="text-xs text-blue-600 font-medium">Apply</button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Mobile Optimization</p>
                      <p className="text-sm text-gray-600">iOS users convert 23% better</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-green-600">+$1.8K/day</p>
                      <button className="text-xs text-blue-600 font-medium">Apply</button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Social Amplification</p>
                      <p className="text-sm text-gray-600">Reward social sharing more</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-green-600">+$3.4K/day</p>
                      <button className="text-xs text-blue-600 font-medium">Apply</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Performance Predictions */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h4 className="font-semibold text-gray-900 mb-4">📈 Performance Predictions</h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-medium text-gray-800 mb-3">Next 7 Days Forecast</h5>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={spendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="predicted" stroke="#8b5cf6" strokeWidth={2} strokeDasharray="5 5" />
                      <Line type="monotone" dataKey="actual" stroke="#06b6d4" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                
                <div>
                  <h5 className="font-medium text-gray-800 mb-3">Scenario Analysis</h5>
                  <div className="space-y-3">
                    <div className="p-3 border border-green-200 rounded-lg">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-green-800">Optimistic Scenario</span>
                        <span className="text-green-600 font-bold">+34%</span>
                      </div>
                      <p className="text-sm text-green-700">If all recommendations applied</p>
                      <p className="text-xs text-green-600 mt-1">Est. weekly revenue: $94K</p>
                    </div>
                    
                    <div className="p-3 border border-blue-200 rounded-lg">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-blue-800">Conservative Scenario</span>
                        <span className="text-blue-600 font-bold">+12%</span>
                      </div>
                      <p className="text-sm text-blue-700">Current trajectory maintained</p>
                      <p className="text-xs text-blue-600 mt-1">Est. weekly revenue: $78K</p>
                    </div>
                    
                    <div className="p-3 border border-amber-200 rounded-lg">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-amber-800">Risk Scenario</span>
                        <span className="text-amber-600 font-bold">-8%</span>
                      </div>
                      <p className="text-sm text-amber-700">If no optimizations made</p>
                      <p className="text-xs text-amber-600 mt-1">Est. weekly revenue: $64K</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Competitive Intelligence */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h4 className="font-semibold text-gray-900 mb-4">🎪 Market Intelligence</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-indigo-600 mb-2" />
                  <p className="font-medium text-indigo-900">Category Trending Up</p>
                  <p className="text-sm text-indigo-700">QSR gaming campaigns +23% this week</p>
                  <p className="text-xs text-indigo-600 mt-1">Optimal time to increase spend</p>
                </div>
                
                <div className="bg-emerald-50 p-4 rounded-lg">
                  <Award className="w-5 h-5 text-emerald-600 mb-2" />
                  <p className="font-medium text-emerald-900">Benchmark Leader</p>
                  <p className="text-sm text-emerald-700">You rank #2 in authenticity scores</p>
                  <p className="text-xs text-emerald-600 mt-1">Push to #1 with mobile optimization</p>
                </div>
                
                <div className="bg-rose-50 p-4 rounded-lg">
                  <Target className="w-5 h-5 text-rose-600 mb-2" />
                  <p className="font-medium text-rose-900">Market Gap</p>
                  <p className="text-sm text-rose-700">Evening hours underserved by category</p>
                  <p className="text-xs text-rose-600 mt-1">Opportunity for market capture</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'benchmarks' && (
          <div className="space-y-6">
            {/* Category Performance */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Live Category Benchmarks</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">Top 15%</div>
                  <div className="text-sm text-gray-500">Category Position</div>
                  <div className="mt-2 text-xs text-gray-400">QSR Gaming Campaigns</div>
                </div>
                
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">+23%</div>
                  <div className="text-sm text-gray-500">Above Average</div>
                  <div className="mt-2 text-xs text-gray-400">Engagement Rate</div>
                </div>
                
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600">87/100</div>
                  <div className="text-sm text-gray-500">Authenticity Score</div>
                  <div className="mt-2 text-xs text-gray-400">Category Range: 65-94</div>
                </div>
              </div>
            </div>

            {/* Performance Metrics Comparison */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-4">Engagement Metrics</h4>
                <div className="space-y-4">
                  {[
                    { metric: 'Check-in Rate', yours: 23.4, category: 18.7, top: 28.1 },
                    { metric: 'Session Extension', yours: 15.2, category: 12.1, top: 19.3 },
                    { metric: 'Return Rate (24h)', yours: 42.1, category: 38.5, top: 47.2 },
                    { metric: 'Social Sharing', yours: 8.7, category: 6.2, top: 11.4 }
                  ].map((item, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">{item.metric}</span>
                        <span className="text-purple-600 font-semibold">{item.yours}%</span>
                      </div>
                      <div className="relative h-2 bg-gray-200 rounded-full">
                        <div 
                          className="absolute h-2 bg-gray-400 rounded-full" 
                          style={{ width: `${(item.category / item.top) * 100}%` }}
                        ></div>
                        <div 
                          className="absolute h-2 bg-purple-500 rounded-full" 
                          style={{ width: `${(item.yours / item.top) * 100}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Category avg: {item.category}%</span>
                        <span>Top quartile: {item.top}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-4">Demographics vs Category</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={demographics}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      dataKey="value"
                    >
                      {demographics.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center space-x-4 mt-4">
                  {demographics.map((item, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: item.color }}
                      ></div>
                      <span className="text-sm text-gray-600">{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Market Insights */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h4 className="font-semibold text-gray-900 mb-4">Category Intelligence</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-blue-600 mb-2" />
                  <p className="font-medium text-blue-900">Peak Hours</p>
                  <p className="text-sm text-blue-700">QSR category performs best 11am-1pm, 6pm-8pm</p>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg">
                  <Award className="w-5 h-5 text-green-600 mb-2" />
                  <p className="font-medium text-green-900">Top Strategy</p>
                  <p className="text-sm text-green-700">Achievement-based rewards show 40% higher engagement</p>
                </div>
                
                <div className="bg-purple-50 p-4 rounded-lg">
                  <Target className="w-5 h-5 text-purple-600 mb-2" />
                  <p className="font-medium text-purple-900">Resource Mix</p>
                  <p className="text-sm text-purple-700">Top performers use 60/40 gold/energy allocation</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'engagement' && (
          <div className="space-y-6">
            {/* Player Behavior Analytics */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Player Engagement Deep Dive</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-600">4.2m</div>
                  <div className="text-sm text-blue-800">Building Discoveries</div>
                  <div className="text-xs text-blue-600 mt-1">+18% vs last week</div>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">23.4%</div>
                  <div className="text-sm text-green-800">Check-in Rate</div>
                  <div className="text-xs text-green-600 mt-1">Category avg: 18.7%</div>
                </div>
                
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-purple-600">6,891</div>
                  <div className="text-sm text-purple-800">Premium Check-ins</div>
                  <div className="text-xs text-purple-600 mt-1">On-location bonus</div>
                </div>
                
                <div className="bg-amber-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-amber-600">42.1%</div>
                  <div className="text-sm text-amber-800">24h Return Rate</div>
                  <div className="text-xs text-amber-600 mt-1">Top 10% performer</div>
                </div>
              </div>

              {/* Session Analytics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Session Extension Impact</h4>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={engagementData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" />
                      <YAxis />
                      <Tooltip />
                      <Area type="monotone" dataKey="engagement" stroke="#8b5cf6" fill="#a855f7" fillOpacity={0.6} />
                    </AreaChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-600 mt-2">
                    Players spend avg. +15.2 minutes per session after brand interaction
                  </p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Player Journey Flow</h4>
                  <div className="space-y-4">
                    {[
                      { stage: 'Building Discovery', players: 100000, rate: '100%', color: 'bg-blue-500' },
                      { stage: 'Initial Check-in', players: 23400, rate: '23.4%', color: 'bg-green-500' },
                      { stage: 'Return Visit (24h)', players: 9851, rate: '42.1%', color: 'bg-purple-500' },
                      { stage: 'Premium Check-in', players: 6891, rate: '29.4%', color: 'bg-amber-500' },
                      { stage: 'Social Share', players: 2034, rate: '8.7%', color: 'bg-red-500' }
                    ].map((stage, index) => (
                      <div key={index} className="flex items-center space-x-4">
                        <div className={`w-3 h-3 rounded-full ${stage.color}`}></div>
                        <div className="flex-1">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">{stage.stage}</span>
                            <span className="text-sm text-gray-600">{stage.players.toLocaleString()}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                            <div 
                              className={`h-2 rounded-full ${stage.color}`}
                              style={{ width: stage.rate }}
                            ></div>
                          </div>
                        </div>
                        <span className="text-sm font-medium text-gray-700 w-12">{stage.rate}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Social & Viral Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-4">Social Engagement</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Share2 className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-blue-900">Organic Shares</p>
                        <p className="text-sm text-blue-700">2,034 players shared experiences</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-blue-600">8.7%</p>
                      <p className="text-xs text-blue-500">share rate</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Users className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-900">Friend Referrals</p>
                        <p className="text-sm text-green-700">847 new players from referrals</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">2.4x</p>
                      <p className="text-xs text-green-500">viral coefficient</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Heart className="w-5 h-5 text-purple-600" />
                      <div>
                        <p className="font-medium text-purple-900">Player Sentiment</p>
                        <p className="text-sm text-purple-700">91% positive brand mentions</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-purple-600">91%</p>
                      <p className="text-xs text-purple-500">positive</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-4">Authenticity Analysis</h4>
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-green-600 mb-2">91/100</div>
                    <div className="text-sm text-gray-600">Authenticity Score</div>
                    <div className="text-xs text-gray-500">23 points above category avg</div>
                  </div>
                  
                  <div className="space-y-3">
                    {[
                      { metric: 'Natural Integration', score: 94, desc: 'Players don\'t feel interrupted' },
                      { metric: 'Gameplay Enhancement', score: 89, desc: 'Brand adds value to experience' },
                      { metric: 'Reward Satisfaction', score: 92, desc: 'Players happy with rewards' },
                      { metric: 'Friction Index', score: 95, desc: 'Minimal gameplay disruption' }
                    ].map((item, index) => (
                      <div key={index} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">{item.metric}</span>
                          <span className="text-green-600 font-semibold">{item.score}/100</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 bg-green-500 rounded-full" 
                            style={{ width: `${item.score}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Player Behavior Insights */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h4 className="font-semibold text-gray-900 mb-4">Player Behavior Insights</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h5 className="font-medium text-gray-800 mb-3">Peak Engagement Times</h5>
                  <div className="space-y-2">
                    {[
                      { time: '11:00 AM - 1:00 PM', engagement: '89%', type: 'Lunch Break' },
                      { time: '6:00 PM - 8:00 PM', engagement: '94%', type: 'Dinner Time' },
                      { time: '9:00 PM - 11:00 PM', engagement: '76%', type: 'Evening Wind-down' }
                    ].map((slot, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div>
                          <p className="text-sm font-medium">{slot.time}</p>
                          <p className="text-xs text-gray-500">{slot.type}</p>
                        </div>
                        <span className="text-sm font-bold text-blue-600">{slot.engagement}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h5 className="font-medium text-gray-800 mb-3">Player Segments</h5>
                  <div className="space-y-2">
                    {[
                      { segment: 'Casual Players', percent: 45, behavior: 'Quick check-ins' },
                      { segment: 'Regular Users', percent: 35, behavior: 'Daily engagement' },
                      { segment: 'Power Players', percent: 20, behavior: 'Extended sessions' }
                    ].map((segment, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div>
                          <p className="text-sm font-medium">{segment.segment}</p>
                          <p className="text-xs text-gray-500">{segment.behavior}</p>
                        </div>
                        <span className="text-sm font-bold text-purple-600">{segment.percent}%</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h5 className="font-medium text-gray-800 mb-3">Location Performance</h5>
                  <div className="space-y-2">
                    {[
                      { location: 'Urban Centers', performance: '+34%', note: 'High foot traffic' },
                      { location: 'Suburban Areas', performance: '+18%', note: 'Family-focused' },
                      { location: 'College Towns', performance: '+52%', note: 'Young demographics' }
                    ].map((loc, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div>
                          <p className="text-sm font-medium">{loc.location}</p>
                          <p className="text-xs text-gray-500">{loc.note}</p>
                        </div>
                        <span className="text-sm font-bold text-green-600">{loc.performance}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'assets' && (
          <div className="space-y-6">
            {/* Custom Asset Tracking */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Custom Asset Performance</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg p-4 text-white">
                  <h4 className="font-semibold mb-2">McFlurry QR Codes</h4>
                  <div className="text-2xl font-bold">8,247</div>
                  <div className="text-sm opacity-80">Redeemed Today</div>
                  <div className="text-xs opacity-70 mt-1">47,832 lifetime • 15,000 available</div>
                </div>
                
                <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-4 text-white">
                  <h4 className="font-semibold mb-2">Golden Arches Badge</h4>
                  <div className="text-2xl font-bold">12,458</div>
                  <div className="text-sm opacity-80">Players Earned</div>
                  <div className="text-xs opacity-70 mt-1">78% completion rate</div>
                </div>
                
                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
                  <h4 className="font-semibold mb-2">Location Check-ins</h4>
                  <div className="text-2xl font-bold">6,891</div>
                  <div className="text-sm opacity-80">Premium Rewards</div>
                  <div className="text-xs opacity-70 mt-1">+150% resource bonus</div>
                </div>
                
                <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg p-4 text-white relative overflow-hidden">
                  <div className="absolute top-2 right-2 bg-white/20 rounded-full px-2 py-1">
                    <span className="text-xs font-bold">PREMIUM</span>
                  </div>
                  <h4 className="font-semibold mb-2">🥷 Flying Ninja Monkey</h4>
                  <div className="text-2xl font-bold">127</div>
                  <div className="text-sm opacity-80">Characters Unlocked</div>
                  <div className="text-xs opacity-70 mt-1">347 lifetime • 30 QR req'd</div>
                </div>
              </div>

              {/* Special Character Tracking */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 mb-6 border border-purple-200">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="text-3xl">🥷</div>
                  <div>
                    <h4 className="text-lg font-semibold text-purple-900">Flying Ninja Monkey Warrior Campaign</h4>
                    <p className="text-sm text-purple-700">Exclusive character unlock for 30 QR code redemptions</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div className="bg-white rounded-lg p-4 text-center border border-purple-200">
                    <div className="text-2xl font-bold text-purple-600">127</div>
                    <div className="text-sm text-purple-800">Characters Today</div>
                    <div className="text-xs text-purple-600 mt-1">+23 vs yesterday</div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 text-center border border-purple-200">
                    <div className="text-2xl font-bold text-purple-600">892</div>
                    <div className="text-sm text-purple-800">Characters This Week</div>
                    <div className="text-xs text-purple-600 mt-1">+34% vs last week</div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 text-center border border-purple-200">
                    <div className="text-2xl font-bold text-purple-600">347</div>
                    <div className="text-sm text-purple-800">Lifetime Unlocks</div>
                    <div className="text-xs text-purple-600 mt-1">Since campaign launch</div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 text-center border border-purple-200">
                    <div className="text-2xl font-bold text-green-600">$1,847</div>
                    <div className="text-sm text-purple-800">Avg Revenue Per Unlock</div>
                    <div className="text-xs text-purple-600 mt-1">Based on purchase correlation</div>
                  </div>
                </div>

                {/* Redemption Funnel */}
                <div className="bg-white rounded-lg p-4 border border-purple-200">
                  <h5 className="font-medium text-purple-900 mb-3">Character Unlock Funnel</h5>
                  <div className="space-y-3">
                    {[
                      { stage: 'Started Journey (1+ QR)', players: 47832, percent: 100, color: 'bg-blue-500' },
                      { stage: 'Halfway Point (15 QRs)', players: 8947, percent: 18.7, color: 'bg-green-500' },
                      { stage: 'Almost There (25 QRs)', players: 1823, percent: 3.8, color: 'bg-amber-500' },
                      { stage: 'Character Unlocked (30 QRs)', players: 347, percent: 0.73, color: 'bg-purple-500' }
                    ].map((stage, index) => (
                      <div key={index} className="flex items-center space-x-4">
                        <div className="w-4 h-4 rounded-full bg-purple-400 flex-shrink-0"></div>
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-medium text-gray-800">{stage.stage}</span>
                            <span className="text-sm text-gray-600">{stage.players.toLocaleString()} players</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${stage.color}`}
                              style={{ width: `${stage.percent}%` }}
                            ></div>
                          </div>
                          <div className="flex justify-between items-center mt-1">
                            <span className="text-xs text-gray-500">{stage.percent}% conversion</span>
                            {index < 3 && (
                              <span className="text-xs text-red-600 font-medium">
                                {((stage.players - 347) * 1847).toLocaleString('en-US', { style: 'currency', currency: 'USD' })} potential revenue
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Smart Insights for Character Campaign */}
                <div className="mt-4 bg-indigo-50 rounded-lg p-4 border-l-4 border-indigo-400">
                  <div className="flex items-start space-x-3">
                    <Zap className="w-5 h-5 text-indigo-600 mt-0.5" />
                    <div>
                      <h6 className="font-medium text-indigo-900">💡 Character Campaign Insights</h6>
                      <div className="mt-2 space-y-1 text-sm text-indigo-800">
                        <p>• <strong>8,947 players</strong> are halfway to unlocking (massive revenue opportunity!)</p>
                        <p>• <strong>Drop-off spike</strong> at 15 QRs suggests reward fatigue - consider milestone bonus</p>
                        <p>• <strong>Average unlock value: $1,847</strong> in correlated purchases - extremely high-value users</p>
                        <p>• <strong>Weekend players</strong> 2.3x more likely to complete the journey</p>
                      </div>
                      <button className="mt-3 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700">
                        Optimize Character Campaign
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Asset Analytics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Redemption Trends</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={engagementData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="engagement" stroke="#ef4444" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Geographic Distribution</h4>
                  <div className="space-y-3">
                    {[
                      { city: 'Los Angeles', redemptions: 1847, percent: 22 },
                      { city: 'Chicago', redemptions: 1534, percent: 19 },
                      { city: 'New York', redemptions: 1423, percent: 17 },
                      { city: 'Houston', redemptions: 1156, percent: 14 },
                      { city: 'Phoenix', redemptions: 987, percent: 12 }
                    ].map((location, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium">{location.city}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-20 h-2 bg-gray-200 rounded-full">
                            <div 
                              className="h-2 bg-red-500 rounded-full" 
                              style={{ width: `${location.percent}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-600 w-12">{location.redemptions}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Fraud Detection */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h4 className="font-semibold text-gray-900 mb-4">Security & Fraud Detection</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-900">Clean Activity</p>
                    <p className="text-sm text-green-700">No suspicious patterns detected</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                  <Eye className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-900">Verification Rate</p>
                    <p className="text-sm text-blue-700">94.7% authentic redemptions</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <Clock className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">Response Time</p>
                    <p className="text-sm text-gray-700">Average: 1.2 seconds</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FNMSponsorDashboard;